// src/components/OfficerSearchBox.jsx
// One search box for Management. Answers "is this officer verified?" in the
// dropdown itself, so the common lookup takes no clicks at all. Selecting a
// suggestion opens that officer's full record.

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Box,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  CircularProgress,
  ClickAwayListener,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import axios from 'axios';

const MIN_CHARS = 3;
const SHOW_MAX = 8;      // a longer list means the search wasn't specific enough
const DEBOUNCE_MS = 350;

const authCfg = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

/* attendanceStatus defaults to DID_NOT_APPEAR for every officer, so it only
   means "absent" once their command has actually closed. */
const statusFor = (p) => {
  if (p.verificationStatus === 'VERIFIED') {
    return { label: 'Verified', color: 'success', icon: <CheckCircleIcon fontSize="small" /> };
  }
  if (p.command?.status === 'COMPLETED' && p.attendanceStatus === 'DID_NOT_APPEAR') {
    return { label: 'Did Not Appear', color: 'error', icon: <CancelIcon fontSize="small" /> };
  }
  return { label: 'Not Yet Verified', color: 'warning', icon: <PendingIcon fontSize="small" /> };
};

const OfficerSearchBox = () => {
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [highlight, setHighlight] = useState(-1);

  const boxRef = useRef(null);

  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_CHARS) {
      setResults([]); setTotal(0); setOpen(false); setError('');
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(
          `/api/management/personnel/search?q=${encodeURIComponent(q)}`,
          authCfg()
        );
        if (cancelled) return;
        const all = res.data.data.results || [];
        setResults(all.slice(0, SHOW_MAX));
        setTotal(all.length);
        setHighlight(-1);
        setOpen(true);
        setError('');
      } catch (err) {
        if (cancelled) return;
        setResults([]); setTotal(0); setOpen(true);
        setError(err.response?.data?.message || 'Search failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  const choose = (person) => {
    setOpen(false);
    navigate(`/lookup?id=${person.id}`);
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h <= 0 ? results.length - 1 : h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(results[highlight >= 0 ? highlight : 0]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Paper sx={{ p: 3, mb: 4, position: 'relative', overflow: 'visible' }} ref={boxRef}>
        <Typography variant="h6" fontWeight="bold" gutterBottom
          sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '1rem' }}>
          Find an Officer
        </Typography>

        <TextField
          fullWidth
          size="small"
          autoComplete="off"
          placeholder="Start typing a service number, IPPIS number, account number or name"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => { if (results.length) setOpen(true); }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            endAdornment: loading ? <CircularProgress size={18} /> : null,
          }}
        />

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Results appear as you type. Use the arrow keys and Enter, or click a name to open the full record.
        </Typography>

        {open && (
          <Paper elevation={6}
            sx={{
              position: 'absolute', left: 24, right: 24, top: 108, zIndex: 20,
              border: '1px solid', borderColor: 'grey.300', maxHeight: 420, overflowY: 'auto',
            }}>
            {error && (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="error.main">{error}</Typography>
              </Box>
            )}

            {!error && results.length === 0 && !loading && (
              <Box sx={{ p: 2.5, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No officer found for “{query.trim()}”
                </Typography>
              </Box>
            )}

            {results.map((p, i) => {
              const st = statusFor(p);
              return (
                <Box
                  key={p.id}
                  onClick={() => choose(p)}
                  onMouseEnter={() => setHighlight(i)}
                  sx={{
                    px: 2, py: 1.25, cursor: 'pointer',
                    borderBottom: '1px solid', borderColor: 'grey.100',
                    bgcolor: highlight === i ? 'action.hover' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                  }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight="bold" noWrap>
                      {p.fullName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {p.serviceNumber} &nbsp;·&nbsp; {p.ippisNumber} &nbsp;·&nbsp; {p.rankName}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" noWrap display="block">
                      {p.command?.code} — {p.command?.zone}
                    </Typography>
                  </Box>
                  <Chip icon={st.icon} label={st.label} color={st.color}
                    size="small" sx={{ flexShrink: 0 }} />
                </Box>
              );
            })}

            {total > SHOW_MAX && (
              <Box sx={{ px: 2, py: 1.25, bgcolor: 'grey.50' }}>
                <Typography variant="caption" color="text.secondary">
                  Showing {SHOW_MAX} of {total}. Add the service number or IPPIS number to narrow it down.
                </Typography>
              </Box>
            )}
          </Paper>
        )}
      </Paper>
    </ClickAwayListener>
  );
};

export default OfficerSearchBox;